import { useEffect, useRef } from 'react';
import { GameState, GameSettings } from '@/types/game';
import { playNotification } from '@/lib/notifications';
import { STORAGE_KEYS } from '@/lib/constants';

interface UseGameTimerProps {
  gameState: GameState;
  setGameState: React.Dispatch<React.SetStateAction<GameState>>;
  settings: GameSettings;
}

export function useGameTimer({ gameState, setGameState, settings }: UseGameTimerProps) {
  const lastTickRef = useRef<number>(Date.now());
  const hasPlayedNotificationRef = useRef<boolean>(false);

  useEffect(() => {
    if (!gameState.isRunning) {
      return;
    }

    // Initialize the reference time when timer starts
    lastTickRef.current = Date.now();

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - lastTickRef.current) / 1000);
      
      if (elapsed > 0) {
        lastTickRef.current = now;
        
        setGameState(prev => {
          // Handle break timer
          if (prev.isHalfTimeBreak) {
            const newBreakTime = Math.max(0, prev.breakTimeRemaining - elapsed);
            
            // Play notification when break ends
            if (newBreakTime === 0 && prev.breakTimeRemaining > 0) {
              playNotification(settings.notificationType);
              hasPlayedNotificationRef.current = true;
            }
            
            return {
              ...prev,
              breakTimeRemaining: newBreakTime,
              isRunning: newBreakTime > 0,
            };
          }
          
          // Handle game timer
          const newTime = Math.max(0, prev.timeRemaining - elapsed);
          
          // Play notification when time reaches 0
          if (newTime === 0 && prev.timeRemaining > 0) {
            playNotification(settings.notificationType);
            hasPlayedNotificationRef.current = true;
            
            // Save game if this is the final half
            const isFinalHalf = prev.currentHalf >= settings.numberOfHalves && !settings.hasOvertime;
            if (isFinalHalf) {
              try {
                const historyJson = localStorage.getItem(STORAGE_KEYS.GAME_HISTORY);
                const history = historyJson ? JSON.parse(historyJson) : [];
                
                const savedGame = {
                  id: Date.now().toString(),
                  timestamp: Date.now(),
                  settings,
                  finalScore: {
                    home: prev.homeScore,
                    away: prev.awayScore,
                  },
                  cards: prev.cards,
                  notes: prev.notes,
                };
                
                history.unshift(savedGame);
                localStorage.setItem(STORAGE_KEYS.GAME_HISTORY, JSON.stringify(history));
              } catch (err) {
                console.error('Failed to save game to history:', err);
              }
            }
          }
          
          return {
            ...prev,
            timeRemaining: newTime,
            isRunning: newTime > 0,
          };
        });
      }
    }, 100);

    return () => clearInterval(interval);
  }, [gameState.isRunning, gameState.isHalfTimeBreak, setGameState, settings]);

  // Reset notification flag when timer starts
  useEffect(() => {
    if (gameState.isRunning) {
      hasPlayedNotificationRef.current = false;
    }
  }, [gameState.isRunning]);
}